import { toViewableImageUrl } from './catalogProductImageStorage';

export function getCardCoverUrl(card, orientation = 'horizontal') {
  let url = null;
  if (orientation === 'vertical') {
    url = card?.backgroundImage?.urlVertical || null;
  } else {
    url = card?.backgroundImage?.url || card?.img || card?.image || null;
  }
  return url ? toViewableImageUrl(url) : null;
}

export function getVideoCoverUrl(video) {
  const url = video?.backgroundImage?.url || video?.image || null;
  return url ? toViewableImageUrl(url) : null;
}

export function formatContentTypeLabel(type) {
  if (type === 'live') return 'Aula ao vivo';
  if (type === 'livestream') return 'Livestream';
  return 'Conteúdo (vídeo/áudio)';
}

export function formatOrientationLabel(orientation) {
  return orientation === 'vertical' ? 'vertical (catálogo)' : 'horizontal (16:9)';
}

function formatBrandColorsBlock(colors) {
  if (!colors?.length) return null;

  const lines = colors.map(([label, value]) => `- ${label}: ${value}`);
  return `Identidade visual — cores (Edição):\n${lines.join('\n')}`;
}

export function formatBaseVisualContextBlock(visualReferences = {}) {
  const sections = [];

  const colorsBlock = formatBrandColorsBlock(visualReferences.colors);
  if (colorsBlock) sections.push(colorsBlock);

  if (visualReferences.logoUrl) {
    sections.push(
      `Logo do app:\n${formatPublicImageUrlLine(toViewableImageUrl(visualReferences.logoUrl))}`
    );
  }

  if (visualReferences.bannerUrls?.length) {
    const lines = visualReferences.bannerUrls.map(
      (url, index) => `- Banner ${index + 1}: ${toViewableImageUrl(url)}`
    );
    sections.push(
      `Banners da Home (Edição) — URL pública da imagem em cada linha:\n${lines.join('\n')}`
    );
  }

  return sections.length ? sections.join('\n\n') : null;
}

export function buildCoverImageRulesBlock({
  aspectRatioLabel,
  subjectLabel,
  referenceLabel,
  coverTitle,
  coverTitleLabel,
  strictSiblingTemplate = false,
  attachmentFilename = null,
}) {
  const titleRule = coverTitle
    ? `- ${coverTitleLabel}: "${coverTitle}" — deve aparecer em DESTAQUE na capa, copiado LITERALMENTE (não parafraseie)
- TEXTO NA CAPA — permitido SOMENTE esse título; sem subtítulos, slogans ou textos extras`
    : `- Siga o padrão das capas de referência quanto ao uso de texto`;

  const siblingTemplateRules = strictSiblingTemplate
    ? `
MODO CLONE DE TEMPLATE (100%):
- Trate a capa TEMPLATE MASTER como arquivo fixo — duplique, não redesenhe
- ÚNICA alteração permitida: substituir o texto do título pelo novo título indicado
- PROIBIDO mudar: cor de fundo, tipografia, ícones, gráficos geométricos, posições, proporções
- Se referências usam fundo laranja sólido + losango geométrico, reproduza EXATAMENTE isso — não use bege, serif, fotos ou gradientes
- Ignore cores da identidade global se conflitarem com as capas irmãs
- Cores/logo do app são secundárias: NÃO substituam o padrão já estabelecido nas capas irmãs`
    : '';

  const steps = attachmentFilename
    ? [
        `1) Analise a imagem "${attachmentFilename}" ANEXADA pelo usuário neste chat (template master) — é a referência visual obrigatória`,
        '2) Use a TEMPLATE MASTER como base do clone — mesma composição, paleta, tipografia',
        '3) Cores da identidade global só se compatíveis com as capas irmãs',
        '4) Branding Book ou fotos extras só se o usuário enviar — a imagem anexada vem primeiro',
      ]
    : [
        '1) Use as referências visuais disponíveis (anexo ou URLs); se não tiver nenhuma, peça ao usuário antes de gerar',
        '2) Cores da identidade global só se compatíveis com as capas irmãs',
        '3) Branding Book ou fotos extras só se o usuário enviar',
      ];

  return `IMPORTANTE — antes de gerar a capa:
${steps.join('\n')}
${siblingTemplateRules}

Regras da capa (OBRIGATÓRIO):
- Proporção: ${aspectRatioLabel}
- Item: ${subjectLabel}
${titleRule}
- NÃO invente estilo genérico de marketing se já existem capas irmãs com padrão definido
- Se puder gerar imagens: entregue o arquivo pronto para upload no painel
- Se NÃO puder gerar imagens: descreva brief detalhado (composição, cores, tipografia, elementos) para produção em outra ferramenta`;
}

export function formatModuleCoverReference(moduleTitle, moduleCoverUrl) {
  if (!moduleCoverUrl) return null;

  return `Capa do módulo "${moduleTitle || '(sem título)'}" (referência secundária):\n- URL pública da imagem: ${moduleCoverUrl}`;
}

export function formatPublicImageUrlLine(url) {
  return `- URL pública da imagem: ${url}`;
}

export function formatSecondaryBrandContextBlock(visualReferences = {}) {
  const block = formatBaseVisualContextBlock(visualReferences);
  if (!block) return null;

  return `Identidade visual global do app (referência TERCIÁRIA — NÃO sobrescreva o padrão das capas irmãs se forem diferentes):\n${block}`;
}

/**
 * Nota quando não há imagem para anexar — URLs como fallback.
 */
export function buildPublicImageUrlNotice({ templateMasterUrl, siblingCount = 0 } = {}) {
  const base =
    'IMPORTANTE — referências visuais:\n' +
    '• As URLs abaixo são links públicos diretos (?alt=media).\n' +
    '• Se a IA não conseguir abri-las, peça ao usuário para anexar uma capa de referência manualmente.\n' +
    '• NÃO invente estilo genérico se não tiver referência visual.';

  if (!templateMasterUrl) {
    return `Nota sobre referências visuais:\n${base}`;
  }

  const siblingNote =
    siblingCount >= 2
      ? `Há ${siblingCount} capas irmãs com o mesmo design.`
      : 'Há 1 capa de referência neste grupo.';

  return `Nota sobre referências visuais:
${siblingNote}

${base}
Template principal (URL pública — backup se não houver anexo):
${templateMasterUrl}`;
}

/**
 * Nota no topo do prompt — template master será anexado pelo usuário no chat.
 */
export function buildReferenceImageAttachmentNotice({
  attachmentFilename = 'capa-template-referencia.jpg',
  siblingCount = 0,
} = {}) {
  const siblingNote =
    siblingCount >= 2
      ? `Há ${siblingCount} capas irmãs com o MESMO design entre si.`
      : siblingCount === 1
        ? 'Há 1 capa de referência neste grupo.'
        : 'Use a imagem anexada como referência visual principal.';

  return `Nota sobre referências visuais:
${siblingNote}

IMPORTANTE — como a referência visual chega à IA:
• O usuário ANEXARÁ neste chat a imagem "${attachmentFilename}" (baixada automaticamente pelo painel ao copiar o prompt).
• Essa imagem é o TEMPLATE MASTER — analise o ARQUIVO ANEXADO; é a fonte visual obrigatória.
• Muitas IAs (ChatGPT, Claude ou similar) NÃO conseguem abrir URLs coladas no texto — por isso a imagem vem como anexo.
• Clone o template da imagem anexada; altere SOMENTE o título indicado abaixo.
• Se a imagem "${attachmentFilename}" não estiver anexada nesta conversa, peça ao usuário para anexá-la ANTES de gerar — NÃO invente estilo.
• As URLs listadas abaixo são apenas backup/documentação; a imagem anexada tem prioridade absoluta.`;
}

/** @deprecated Use buildPublicImageUrlNotice */
export const buildAdminAttachmentWorkflow = buildPublicImageUrlNotice;

/**
 * Instruções de clone exato quando já existem capas irmãs com template definido.
 */
export function buildTemplateCloneInstructions({
  coverTitle,
  templateMasterUrl,
  siblingCount = 0,
  scopeLabel = 'módulo',
  attachmentFilename = 'capa-template-referencia.jpg',
  selectedReferenceLabel = null,
}) {
  if (!templateMasterUrl || siblingCount < 1) return '';

  const identicalNote =
    siblingCount >= 2
      ? `As ${siblingCount} capas de referência neste ${scopeLabel} têm o MESMO design entre si.`
      : `Há 1 capa de referência neste ${scopeLabel}.`;

  const chosenNote = selectedReferenceLabel
    ? `\nCapa escolhida como template: "${selectedReferenceLabel}"`
    : '';

  return `═══ MODO CLONE DE TEMPLATE (100% fidelidade) ═══

${identicalNote}${chosenNote} Clone o template e troque SOMENTE o título para: "${coverTitle}"

TEMPLATE MASTER — imagem anexada pelo usuário: "${attachmentFilename}"
(URL pública de backup, caso precise: ${templateMasterUrl})

Para a IA:
→ Analise a imagem "${attachmentFilename}" ANEXADA neste chat — é o template visual obrigatório
→ NÃO dependa das URLs no texto; IAs como ChatGPT, Claude ou similar frequentemente não conseguem abri-las
→ Clone o template da imagem anexada; altere SOMENTE o texto para "${coverTitle}"
→ PROIBIDO: novo layout, "embelezar", trocar tipografia/paleta, adicionar fotos, sombras ou elementos novos
→ Resultado: indistinguível do template anexado, exceto pelo título "${coverTitle}"`;
}
